import { useGoalById, useUpdateGoal } from 'features/goals';
import { useUpdateUserSettings } from 'features/user/hooks/use-update-user-settings';
import { OutletContext } from 'pages/goal-detail/outlet-context';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext, useParams } from 'react-router';
import { Check, Plus, SettingsSort18 } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import { Colors, IconButton } from 'shared/components/ui/icon-button';
import { PageSectionBase } from 'shared/components/ui/page-section-base';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
  PopupMenuTitle,
  PopupMenuToggle,
} from 'shared/components/ui/popup-menu';
import { useOpenCreateGoalDialog } from 'shared/contexts/goal-form';
import { useUser } from 'shared/hooks/use-user';
import { Paths } from 'shared/routes';
import { SortMenuOptions } from 'shared/types/sort-menu-options';
import { GoalSortingMode } from 'shared/types/sorting-mode';
import { replaceUrlParams } from 'shared/utils/replace-url-params';

import { SubgoalsOverview } from './components/subgoals-overview';
import * as Styled from './goal-detail.style';

const sortOptions: SortMenuOptions<GoalSortingMode>[] = [
  {
    id: GoalSortingMode.Custom,
    value: GoalSortingMode.Custom,
    label: `general.labels.goals.sort.options.${GoalSortingMode.Custom}`,
  },
  {
    id: GoalSortingMode.StartDate,
    value: GoalSortingMode.StartDate,
    label: `general.labels.goals.sort.options.${GoalSortingMode.StartDate}`,
  },
  {
    id: GoalSortingMode.Deadline,
    value: GoalSortingMode.Deadline,
    label: `general.labels.goals.sort.options.${GoalSortingMode.Deadline}`,
  },
  {
    id: GoalSortingMode.Progress,
    value: GoalSortingMode.Progress,
    label: `general.labels.goals.sort.options.${GoalSortingMode.Progress}`,
  },
];

export const SummaryRoadmap: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { mainScrollRef } = useOutletContext<OutletContext>();
  const goal = useGoalById(id);
  const { submit: updateGoal } = useUpdateGoal();
  const user = useUser();
  const updateUserSettings = useUpdateUserSettings();
  const interactionsRef = useRef<HTMLDivElement>(null);
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);

  const openCreateGoalDialog = useOpenCreateGoalDialog();

  const showCompletedGoals =
    user?.settings?.goalDetail?.roadmap?.showCompletedGoals ?? true;

  const onShowCompletedGoals = (value: boolean) => {
    updateUserSettings({
      goalDetail: {
        roadmap: {
          showCompletedGoals: value,
        },
      },
    });
  };

  const onAddGoal = () => {
    if (!goal) {
      return;
    }
    openCreateGoalDialog({ parentIds: [goal.id] });
  };

  const onSort = useCallback(
    (value: GoalSortingMode) => {
      if (!goal) {
        return;
      }
      updateGoal({ id: goal.id, goalSortingMode: value });
      setIsSortMenuOpen(false);
    },
    [goal, updateGoal],
  );

  const goalsUrl = useMemo(
    () => replaceUrlParams(Paths.GoalDetailRoadmap, { id: id! }),
    [id],
  );

  if (!goal) {
    return null;
  }

  return (
    <PageSectionBase
      title={t('pages.goal-detail.tabs.roadmap.label')}
      url={goalsUrl}
      interactions={
        <Styled.InteractionsContainer ref={interactionsRef}>
          <IconButton icon={Plus} onClick={onAddGoal} color={Colors.Default} />
          <IconButton
            icon={SettingsSort18}
            onClick={() => setIsSortMenuOpen(true)}
            color={Colors.Default}
          />
          {isSortMenuOpen && (
            <PopupMenu
              referenceElement={interactionsRef}
              onOutside={() => setIsSortMenuOpen(false)}
              position="bottom-end"
            >
              <PopupMenuList hasBottomBorder>
                <PopupMenuListItem>
                  <PopupMenuToggle
                    label={t('general.labels.goals.show-completed-goals')}
                    checked={showCompletedGoals}
                    onChange={onShowCompletedGoals}
                  />
                </PopupMenuListItem>
              </PopupMenuList>
              <PopupMenuList>
                <PopupMenuTitle title={t('general.labels.goals.sort.title')} />
                {sortOptions.map(({ id: optionId, label, value }) => (
                  <PopupMenuListItem key={optionId}>
                    <PopupMenuButton
                      start={
                        (goal.goalSortingMode ?? GoalSortingMode.Custom) ===
                        optionId ? (
                          <Icon icon={Check} />
                        ) : undefined
                      }
                      onClick={() => onSort(value)}
                      indentationLevel={
                        (goal.goalSortingMode ?? GoalSortingMode.Custom) !==
                        optionId
                          ? 1
                          : 0
                      }
                    >
                      {t(label)}
                    </PopupMenuButton>
                  </PopupMenuListItem>
                ))}
              </PopupMenuList>
            </PopupMenu>
          )}
        </Styled.InteractionsContainer>
      }
      noPadding
    >
      <SubgoalsOverview
        goal={goal}
        hideCompleted={!showCompletedGoals}
        scrollElement={mainScrollRef}
        hideArchived
      />
    </PageSectionBase>
  );
};
